/**
 * Product card — zone hover gallery + color siblings.
 */
const HOVER_DWELL_MS = 150;
const MAX_CONCURRENT_PRELOADS = 4;
const isTouchOnly = window.matchMedia("(hover: none) and (pointer: coarse)").matches;

const hoverPreloadQueue = [];
let activeHoverPreloads = 0;

function isHoverImageReady(img) {
  return Boolean(img?.src && !img.dataset.src);
}

function loadHoverImage(img) {
  if (!img?.dataset.src) {
    return Promise.resolve();
  }

  img.src = img.dataset.src;
  img.srcset = img.dataset.srcset || "";
  img.sizes = img.dataset.sizes || "";
  img.removeAttribute("data-src");
  img.removeAttribute("data-srcset");
  img.removeAttribute("data-sizes");
  img.classList.remove("lazy-hover-img");

  return new Promise((resolve, reject) => {
    if (img.complete) {
      resolve();
      return;
    }

    img.addEventListener("load", () => resolve(), { once: true });
    img.addEventListener("error", () => reject(), { once: true });
  });
}

function preloadHoverImage(img) {
  if (!img || isHoverImageReady(img)) {
    return;
  }

  hoverPreloadQueue.push(img);
  drainHoverPreloadQueue();
}

function drainHoverPreloadQueue() {
  while (activeHoverPreloads < MAX_CONCURRENT_PRELOADS && hoverPreloadQueue.length > 0) {
    const img = hoverPreloadQueue.shift();
    activeHoverPreloads += 1;

    loadHoverImage(img)
      .catch(() => {})
      .finally(() => {
        activeHoverPreloads -= 1;
        drainHoverPreloadQueue();
      });
  }
}

function preloadAllHoverImages(images) {
  images.slice(1).forEach((img) => preloadHoverImage(img));
}

class ProductCardHover {
  constructor() {
    this.initializedContainers = new WeakSet();
    this.observer = null;
    this.init();
  }

  init() {
    this.setupExistingContainers();

    this.observer = new MutationObserver(() => {
      this.setupExistingContainers();
    });

    this.observer.observe(document.body, { childList: true, subtree: true });

    if ("IntersectionObserver" in window) {
      this.viewportObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) {
              return;
            }

            const images = Array.from(entry.target.querySelectorAll(".product-hover-img"));

            if (images[1]) {
              preloadHoverImage(images[1]);
            }

            this.viewportObserver.unobserve(entry.target);
          });
        },
        { rootMargin: "200px" }
      );

      document.querySelectorAll("[data-product-hover]").forEach((container) => {
        this.viewportObserver.observe(container);
      });
    }
  }

  setupExistingContainers() {
    document.querySelectorAll("[data-product-hover]").forEach((container) => {
      if (this.initializedContainers.has(container)) {
        return;
      }

      this.initializedContainers.add(container);
      this.setupContainer(container);
    });
  }

  setupContainer(container) {
    const images = Array.from(container.querySelectorAll(".product-hover-img"));
    const zonesContainer = container.querySelector(".product-hover-zones");

    if (!images.length || !zonesContainer) {
      return;
    }

    this._revealImage(container, 0, images);

    if (!isTouchOnly) {
      container.addEventListener("mouseenter", () => {
        preloadAllHoverImages(images);
      });

      zonesContainer.querySelectorAll(".hover-zone").forEach((hoverZone) => {
        hoverZone.addEventListener("mouseenter", () => {
          const zoneIndex = Number.parseInt(hoverZone.dataset.zone, 10);

          window.clearTimeout(container._hoverTimer);
          container._hoverTimer = window.setTimeout(() => {
            this._showImageAtIndex(container, zoneIndex, images);
          }, HOVER_DWELL_MS);
        });
      });

      container.addEventListener("mouseleave", () => {
        window.clearTimeout(container._hoverTimer);
        this._resetToFirst(container, images);
      });
    }

    this.setupSwipe(container, images);

    if (this.viewportObserver) {
      this.viewportObserver.observe(container);
    }
  }

  setupSwipe(container, images) {
    let touchStartX = 0;
    let touchStartY = 0;
    let touchStartTime = 0;
    let isSwiping = false;

    container.addEventListener(
      "touchstart",
      (event) => {
        if (event.touches.length !== 1) {
          return;
        }

        touchStartX = event.touches[0].clientX;
        touchStartY = event.touches[0].clientY;
        touchStartTime = Date.now();
        isSwiping = false;
        preloadAllHoverImages(images);
      },
      { passive: true }
    );

    container.addEventListener(
      "touchmove",
      (event) => {
        if (event.touches.length !== 1) {
          return;
        }

        const diffX = Math.abs(event.touches[0].clientX - touchStartX);
        const diffY = Math.abs(event.touches[0].clientY - touchStartY);

        if (diffX > 10 && diffX > diffY) {
          isSwiping = true;
        }
      },
      { passive: true }
    );

    container.addEventListener("touchend", (event) => {
      if (!isSwiping) {
        return;
      }

      const diffX = event.changedTouches[0].clientX - touchStartX;
      const elapsed = Date.now() - touchStartTime;

      if (Math.abs(diffX) < 20 || elapsed > 800) {
        return;
      }

      const currentIndex = Number.parseInt(container.getAttribute("data-current-image"), 10) || 0;
      const nextIndex = diffX < 0
        ? Math.min(currentIndex + 1, images.length - 1)
        : Math.max(currentIndex - 1, 0);

      this._showImageAtIndex(container, nextIndex, images);
    });
  }

  _revealImage(container, index, images) {
    const activeIndex = Number.parseInt(container.getAttribute("data-current-image"), 10) || 0;

    if (activeIndex !== index) {
      return;
    }

    images.forEach((imgEl, imgIndex) => {
      const isActive = imgIndex === index;
      imgEl.style.setProperty("opacity", isActive ? "1" : "0", "important");
      imgEl.style.setProperty("z-index", isActive ? "2" : "1", "important");
    });

    this._updateDots(container, index);
  }

  _showImageAtIndex(container, index, images) {
    if (container.dataset.colorPreviewActive === "true") return;
    const currentIndex = Number.parseInt(container.getAttribute("data-current-image"), 10) || 0;

    if (currentIndex === index || !images[index]) {
      return;
    }

    container.setAttribute("data-current-image", index.toString());

    if (images[index + 1]) {
      preloadHoverImage(images[index + 1]);
    }

    if (images[index - 1]) {
      preloadHoverImage(images[index - 1]);
    }

    const img = images[index];

    if (isHoverImageReady(img)) {
      this._revealImage(container, index, images);
      return;
    }

    loadHoverImage(img)
      .then(() => {
        this._revealImage(container, index, images);
      })
      .catch(() => {});
  }

  _resetToFirst(container, images) {
    container.setAttribute("data-current-image", "0");
    this._revealImage(container, 0, images);
  }

  _updateDots(container, activeIndex) {
    container.querySelectorAll(".hover-dot").forEach((dot, index) => {
      dot.classList.toggle("active", index === activeIndex);
    });
  }

  resetContainer(container) {
    const images = Array.from(container?.querySelectorAll(".product-hover-img") ?? []);

    if (images.length) {
      this._resetToFirst(container, images);
    }
  }
}

class ProductCardColors {
  init() {
    if (this.initialized) return;
    this.initialized = true;
    document.addEventListener("click", (event) => {
      const toggle = event.target.closest("[data-colors-more]");
      const chip = event.target.closest(".product-card__color-chip");
      if (toggle) {
        event.preventDefault();
        const container = toggle.closest(".product-card__color-siblings");
        this.setExpanded(container, !container.classList.contains("is-expanded"));
      } else if (chip) {
        event.preventDefault();
        const container = chip.closest(".product-card__color-siblings");
        const card = chip.closest("product-card");
        const image = card?.querySelector(".product-card__image--primary");
        if (!image || !chip.dataset.cardImage) return;
        container.querySelectorAll(".product-card__color-chip").forEach((button) => {
          button.classList.toggle("is-active", button === chip);
          button.setAttribute("aria-pressed", String(button === chip));
        });
        image.src = chip.dataset.cardImage;
        image.srcset = chip.dataset.cardImageSrcset || "";
        const mediaLink = card.querySelector(".product-card__media");
        if (mediaLink && chip.dataset.cardUrl) mediaLink.href = chip.dataset.cardUrl;
        if (chip.dataset.cardAvailable) card.dataset.productAvailable = chip.dataset.cardAvailable;
        const hover = card.querySelector("[data-product-hover]");
        if (hover) {
          if (chip === container.querySelector(".product-card__color-chip")) delete hover.dataset.colorPreviewActive;
          else hover.dataset.colorPreviewActive = "true";
          window.clearTimeout(hover._hoverTimer);
          productCardHover.resetContainer(hover);
        }
      } else {
        document.querySelectorAll(".product-card__color-siblings.is-expanded").forEach((container) => this.setExpanded(container, false));
      }
    });
    document.addEventListener("keydown", (event) => {
      if (event.key !== "Escape") return;
      const container = event.target.closest(".product-card__color-siblings");
      if (container) {
        this.setExpanded(container, false);
        container.querySelector("[data-colors-more]")?.focus();
      }
    });
  }

  setExpanded(container, expanded) {
    if (!container) return;
    container.classList.toggle("is-expanded", expanded);
    container.querySelector("[data-colors-more]")?.setAttribute("aria-expanded", String(expanded));
  }
}

const productCardHover = new ProductCardHover();
const productCardColors = new ProductCardColors();
productCardColors.init();

class ProductCardCampaignBadge extends HTMLElement {
  connectedCallback() {
    this.texts = Array.from(this.querySelectorAll(".product-card__campaign-badge-text"));

    if (this.texts.length <= 1) {
      return;
    }

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }

    this.currentIndex = 0;
    this.intervalMs = Number(this.dataset.interval) || 5000;
    this.timer = window.setInterval(() => this.rotate(), this.intervalMs);
  }

  disconnectedCallback() {
    window.clearInterval(this.timer);
  }

  rotate() {
    this.texts[this.currentIndex]?.classList.remove("is-active");
    this.currentIndex = (this.currentIndex + 1) % this.texts.length;
    this.texts[this.currentIndex]?.classList.add("is-active");
  }
}

if (!window.customElements.get("product-card-campaign-badge")) {
  window.customElements.define("product-card-campaign-badge", ProductCardCampaignBadge);
}
